import React, { createContext, ReactNode, useEffect, useRef, useState } from 'react';
import { Authorizer } from '../utils/Authorizer';
import {
  Actions,
  AuthContextType,
  FlowState,
  OAuth2State,
  SessionState,
  TokenState,
  TokenType,
} from '../utils/interfaces';
import {
  createSessionState,
  createTokenState,
  generateOAuth2Nonce,
  generateOAuth2State,
  getRefresh,
  getTokenWithCodeOAuth2,
  logoutOnAuthServer,
} from './AuthHelpers';
import queryString from 'query-string';
import { oauth2Config } from '../data';
import getLogger, { Logger } from '../../log';
import { ImageServiceApi, UsersDTO, UsersServiceApi } from '../../api/admin';
import { getAuthMiddleware } from '../utils/GetAuthMiddleware';

const logger: Logger = getLogger('OAuth2/Container');

export const initialFlowState: FlowState = {state: null};
export const initialTokenState: TokenState = {auth: null, refresh: null};
export const initialSessionState: SessionState = null;

const initialState: OAuth2State = {
  flow: initialFlowState,
  token: initialTokenState,
  session: initialSessionState,
};

export const AuthContext: React.Context<AuthContextType> = createContext<AuthContextType>(null);

export const AuthProvider = ({children}: {children: ReactNode}): ReactNode => {
  const [authState, _setAuthState] = useState<OAuth2State>(initialState);
  const [hydrated, setHydrated] = useState<boolean>(false);
  const [user, setUser] = useState<UsersDTO>(null);
  const [avatar, setAvatar] = useState<Blob>(null);
  const usersByIdCache = useRef<Map<number, UsersDTO>>(new Map());
  const pendingUsersById = useRef<Map<number, Promise<UsersDTO>>>(new Map());
  const cacheSessionUserId = useRef<number>(null);
  const userCacheGeneration = useRef<number>(0);

  const clearUserCache = (): void => {
    userCacheGeneration.current += 1;
    usersByIdCache.current.clear();
    pendingUsersById.current.clear();
  };

  useEffect((): void => {
    const sessionUserId: number = authState.session?.user_id ?? null;
    if (cacheSessionUserId.current !== sessionUserId) {
      clearUserCache();
      cacheSessionUserId.current = sessionUserId;
    }

    localStorage.setItem('auth', JSON.stringify(authState));
    loadUser();
  }, [authState]);

  useEffect((): void => loadAvatar(), [user]);

  const loadAvatar: () => void = (): void => {
    if (!user?.id) return setAvatar(null);
    const imageApi: ImageServiceApi = new ImageServiceApi().withMiddleware(getAuthMiddleware(actions));
    void imageApi
      .downloadFile({ userId: user.id })
      .then((blob: Blob): void => setAvatar(blob))
      .catch((): void => {
        setAvatar(null);
        console.warn('No avatar found.');
      });
  };

  const loadUser: () => void = (): void => {
    if (!authState?.session?.user_id) {
      if (user !== null) setUser(null);
      return;
    }
    loadUsersByIds([authState.session.user_id])
      .then((users: { [key: string]: UsersDTO }): void => {
        const result: UsersDTO = users[authState.session.user_id];
        if (result) setUser(result);
      })
      .catch((): void => {
        if (user !== null) setUser(null);
      });
  };

  const loadUsersByIds = async (userIds: number[]): Promise<{ [key: string]: UsersDTO }> => {
    const cacheGeneration: number = userCacheGeneration.current;
    const uniqueUserIds: number[] = [...new Set(userIds.filter((id: number): boolean => !!id))];
    const users: { [key: string]: UsersDTO } = {};

    const missingUserIds: number[] = uniqueUserIds.filter((id: number): boolean => {
      const cachedUser: UsersDTO = usersByIdCache.current.get(id);
      if (cachedUser) {
        users[id] = cachedUser;
        return false;
      }
      return !pendingUsersById.current.has(id);
    });

    if (missingUserIds.length > 0) {
      const userApi: UsersServiceApi = new UsersServiceApi().withMiddleware(getAuthMiddleware(actions));
      const request: Promise<{ [key: string]: UsersDTO }> = userApi.getUsersByIds({ userIds: missingUserIds });
      missingUserIds.forEach((id: number): void => {
        const pendingUser: Promise<UsersDTO> = request
          .then((result: { [key: string]: UsersDTO }): UsersDTO => {
            const loadedUser: UsersDTO = result[id];
            if (loadedUser && cacheGeneration === userCacheGeneration.current) {
              usersByIdCache.current.set(id, loadedUser);
            }
            return loadedUser;
          })
          .finally((): void => {
            if (cacheGeneration === userCacheGeneration.current) {
              pendingUsersById.current.delete(id);
            }
          });
        pendingUsersById.current.set(id, pendingUser);
      });
    }

    await Promise.all(uniqueUserIds.map(async (id: number): Promise<void> => {
      const cachedUser: UsersDTO = usersByIdCache.current.get(id);
      const loadedUser: UsersDTO = cachedUser || await pendingUsersById.current.get(id);
      if (loadedUser) users[id] = loadedUser;
    }));

    return users;
  };

  const setAuthState: (state: Partial<OAuth2State>) => void = (state: Partial<OAuth2State>): void => {
    _setAuthState({...authState, ...state});
  };

  const hydrate: () => Promise<void> = async (): Promise<void> => {
    logger.debug('Hydrate state: Initializing');
    const data: OAuth2State = JSON.parse(localStorage.getItem('auth')) as OAuth2State;
    const auth: TokenType = data.token?.auth;
    const refresh: TokenType = data.token?.refresh;
    const now: number = new Date().getTime();
    const token: TokenState = {
      auth: auth != null && auth.expires >= now ? auth : null,
      // refresh: refresh != null && refresh.expires >= now ? refresh : null,
      refresh,
    };
    const state: OAuth2State = {
      flow: data.flow || initialFlowState,
      token,
      session: data.session || initialSessionState,
    };
    logger.debug('Hydrate state: State', state);
    setAuthState(state);
    logger.debug('Hydrate state: Done');
    setHydrated(true);
  };

  const logout: () => void = (): void => {
    removeToken();
    void logoutOnAuthServer();
  };

  const getTokenWithCode: (authCode: string, authFlowState: string) => Promise<void> = async (authCode: string, authFlowState: string): Promise<void> => {
    if (authFlowState !== authState.flow.state) {
      logger.warn(`Invalid auth state: ${authFlowState} !== ${authState.flow.state}`);
    }

    setAuthState({flow: {state: generateOAuth2State()}});

    try {
      const {auth, refresh} = await getTokenWithCodeOAuth2(authCode);
      const token: TokenState = createTokenState(auth, refresh);
      const session: SessionState = createSessionState(auth);
      _setAuthState({ token, session, flow: {state: generateOAuth2State()} });
    } catch (ex) {
      removeToken();
      logger.warn('Error while getting auth token', ex);
    }
  };

  const removeToken: () => void = (): void => {
    logger.log('Remove token ...');
    clearUserCache();
    cacheSessionUserId.current = null;
    _setAuthState({ ...authState, token: initialTokenState, session: initialSessionState });
    logger.log('... removed');
  };

  let refreshPromise: Promise<boolean> = null;

  /** Refresh the access_token. If a refresh is already in flight, piggyback that */
  const refreshToken: () => Promise<boolean> = async (): Promise<boolean> => {
    if (refreshPromise === null) {
      try {
        refreshPromise = innerDoRefreshToken();
        return await refreshPromise;
      } finally {
        refreshPromise = null;
      }
    } else {
      return await refreshPromise;
    }
  };

  /** Never call this method directly. Use `refreshToken` instead. */
  const innerDoRefreshToken: () => Promise<boolean> = async (): Promise<boolean> => {
    if (!authState.token?.refresh?.token) {
      return false;
    }

    const innerRefreshToken: string = authState.token.refresh.token;

    logger.log('Refresh token ...');

    try {
      const { auth, refresh } = await getRefresh(innerRefreshToken);
      const token: TokenState = createTokenState(auth, refresh);
      const session: SessionState = createSessionState(auth);
      _setAuthState({
        token,
        session,
        flow: { state: generateOAuth2State() },
      });
      logger.log('... refreshed');
      return true;
    } catch (ex) {
      logger.warn('Error while refreshing auth token', ex);
      removeToken();
    }
  };

  const getAuthUrl: () => string = (): string => {
    const {authUrl, clientId} = oauth2Config;
    const q = {
      client_id: clientId,
      redirect_uri: window.location.protocol + '//' + window.location.host + '/oauth',
      scope: 'read',
      response_type: 'code',
      response_mode: 'query',
      nonce: generateOAuth2Nonce(),
      state: authState.flow.state,
    };
    const qs: string = queryString.stringify(q);
    return `${authUrl}?${qs}`;
  };

  const getAuthToken: (raw?: boolean) => string|undefined = (raw: boolean = false): string | undefined => {
    if (authState.token == null || authState.token.auth == null) return undefined;

    const {token, expires} = authState.token.auth;

    if (expires < Date.now()) {
      console.debug('Auth token expired');
      return undefined;
    }

    return (raw ? '' : 'Bearer ') + token;
  };

  const getAuthTokenOrRefresh: (raw?: boolean) => Promise<string | undefined> = async (
    raw: boolean = false
  ): Promise<string | undefined> => {
    const authToken: string = getAuthToken(raw);
    if (authToken == null) {
      // console.debug('Token is expired, refresh and return the new token');
      await refreshToken();
      const refreshedAuthToken: string = getAuthToken(raw);
      if (refreshedAuthToken == null) {
        // console.warn('Invalid refreshed auth token');
      }
      return refreshedAuthToken;
    }
    return authToken;
  };

  let demologin: Promise<(roles: string[], givenName: string, surname: string, email?: string) => void> = null;
  if(process.env.REACT_APP_DEMO_LOGIN_ENABLED === 'true') {
    demologin = import('./../../pages/components/debug/demologin').then(({_demologin}) => _demologin(setAuthState));
  }

  const actions: Actions = {
    logout,
    getAuthUrl,
    getTokenWithCode,
    removeToken,
    demologin,
    getAuthTokenOrRefresh,
    getAuthToken,
  };

  if (!hydrated) {
    void hydrate();
  }

  const getAuthorizer: () => Authorizer = (): Authorizer => new Authorizer(authState);

  const getSession: () => SessionState = (): SessionState => authState.session;
  const getToken: () => TokenState = (): TokenState => authState.token;

  return <AuthContext.Provider value={{token: getToken(), session: getSession(), authorizer: getAuthorizer(), actions, user, avatar, loadAvatar, loadUsersByIds}}>
    {children}
  </AuthContext.Provider>;
};
